import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import { LinkButton } from '@enamelai/design-system'
import { Icon as ArrowLeftIcon } from '@enamelai/design-system/icons/Arrow_2_left'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportDataTable, {
  ReportDataTableColumn,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import ReportExpandableSection from '@components/pages/Reports/shared/ReportExpandableSection.component'
import { useReportInPlaceExpandOptional } from '@components/pages/Reports/shared/ReportInPlaceExpandContext'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  AR_OPPORTUNITY_CARD_ROW_LIMIT,
  ArUnscheduledOpportunityRow,
  NEW_VS_EXISTING_VIEW_TABS,
  NewVsExistingProductionView,
  PATIENT_VALUE_REVENUE_FILTERS,
  PATIENT_VALUE_REVENUE_KPI_DEFINITIONS,
  PROCEDURE_MIX_COLORS,
} from '@constants/Reports/patientValueRevenue.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { usePatientValueRevenueReport } from '@hooks/reports/usePatientValueRevenueReport'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import { formatCurrency } from '@utils/dashboardFinance.util'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { formatReportKpiFooter } from '@utils/reports/reportKpiFooter.util'
import {
  computeDynamicCountAxis,
  computePercentAxis,
  formatKpiCurrency,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const AR_OPPORTUNITY_TITLE = 'AR & unscheduled treatment opportunity'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'

/**
 * Recharts vertical bar charts: space between category bands (the “columns”).
 * This is a share of each band’s width, so spacing scales with chart width — not a fixed pixel distance.
 * Use the same value across report bar charts so rhythm stays consistent.
 */
const REPORT_BAR_CATEGORY_GAP = '16%'

/** New vs Existing has only two categories — use more of each band so bars read thicker. */
const REPORT_NEW_EXISTING_BAR_CATEGORY_GAP = '6%'
const REPORT_NEW_EXISTING_MAX_BAR_SIZE = 150

const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }

/** Y-axis width (px) — keep only enough for tick labels; wider values steal plot width. */
const REPORT_YAXIS_WIDTH = 34
const REPORT_YAXIS_WIDTH_WIDE = 46

const AR_OPPORTUNITY_UNSCHEDULED_VALUE_COLUMN: ReportDataTableColumn<ArUnscheduledOpportunityRow> =
  {
    key: 'unscheduledValue',
    label: 'Unscheduled value',
    headerClassName:
      'reports-patient-value-revenue__table-head--num reports-patient-value-revenue__ar-unscheduled-col',
    cellClassName:
      'reports-patient-value-revenue__table-cell--num reports-patient-value-revenue__ar-unscheduled-col',
  }

function formatDollarK(value: number): string {
  if (value === 0) return '$0'
  const k = value / 1000
  if (Math.abs(k) >= 1) {
    return Number.isInteger(k) ? `$${k}K` : `$${k.toFixed(1)}K`
  }
  return formatCurrency(value, { compact: true })
}

function formatRevenueKLabel(value: number): string {
  const k = value / 1000
  if (Math.abs(k) >= 1) {
    return `$${k % 1 !== 0 ? k.toFixed(1) : k}K`
  }
  return formatCurrency(value, { compact: true })
}

function PatientValueRevenueReport(): React.ReactElement {
  const expandCtx = useReportInPlaceExpandOptional()
  const arOpportunityFullViewOnly = expandCtx?.expandedSectionId === 'pvr-ar'
  const [valueSegmentsView, setValueSegmentsView] =
    useState<ReportsDistributionView>('Count')
  const [newVsExistingView, setNewVsExistingView] =
    useState<NewVsExistingProductionView>('Patients')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)

  const { loading, error, data } = usePatientValueRevenueReport(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load patient value and revenue data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return PATIENT_VALUE_REVENUE_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer || '—',
        value: loading ? '…' : '—',
        tone: 'neutral',
      }))
    }

    const valueById: Record<string, number> = {
      'avg-ltv': data.averagePatientValue.value,
      'active-revenue': data.activePatientRevenue.value,
      'unscheduled-treatment': data.unscheduledTreatment.value,
      'total-outstanding-ar': data.totalOutstandingAr.value,
    }

    const priorById: Record<string, number> = {
      'avg-ltv': data.averagePatientValueFooter.value,
      'active-revenue': data.activePatientRevenueFooter.value,
      'unscheduled-treatment': data.unscheduledTreatmentFooter.value,
      'total-outstanding-ar': data.totalOutstandingArFooter.value,
    }

    return PATIENT_VALUE_REVENUE_KPI_DEFINITIONS.map((def) => {
      const footerResult = formatReportKpiFooter(
        def.footerMode ?? 'percentChange',
        valueById[def.id],
        priorById[def.id],
      )

      return {
        id: def.id,
        title: def.title,
        footer: footerResult.text,
        tone: footerResult.tone,
        value: formatKpiCurrency(valueById[def.id]),
      }
    })
  }, [data, loading])

  const procedureMixData = useMemo(() => {
    if (!data) return []
    return data.productionByProcedureMix.categories.map((row) => ({
      id: row.id,
      label: row.label,
      amount: formatDollarK(row.amount),
      percentage: row.percentage,
      color: PROCEDURE_MIX_COLORS[row.id] ?? 'var(--gray-400)',
    }))
  }, [data])

  const procedureMixTotal = useMemo(() => {
    if (!data) return loading ? '…' : '—'
    return formatDollarK(data.productionByProcedureMix.total)
  }, [data, loading])

  const valueSegmentRows = useMemo(() => {
    if (!data) return []
    return data.activePatientValueSegments.segments.map((row) => ({
      id: row.id,
      label: row.label,
      count: row.count,
      percentage: row.percentage,
      percentageLabel: `${row.percentage}%`,
    }))
  }, [data])

  const highValueRevenueShare =
    data?.activePatientValueSegments.highValueRevenueShare

  const arOpportunityRows = useMemo((): ArUnscheduledOpportunityRow[] => {
    if (!data) return []
    return data.arUnscheduledByProvider.rows.map((row) => ({
      id: row.providerId,
      provider: row.providerName,
      patients: row.patients,
      unscheduledValue: formatCurrency(row.unscheduledValue),
      outstandingArValue: formatCurrency(row.outstandingAr),
    }))
  }, [data])

  const arOpportunityTableRows = useMemo(
    () =>
      arOpportunityFullViewOnly
        ? arOpportunityRows
        : arOpportunityRows.slice(0, AR_OPPORTUNITY_CARD_ROW_LIMIT),
    [arOpportunityFullViewOnly, arOpportunityRows],
  )

  const handleArOpportunityFullView = (): void => {
    expandCtx?.expand('pvr-ar')
  }

  const handleArOpportunityExitFullView = (): void => {
    expandCtx?.exit()
  }

  const arOpportunityTableColumns: ReportDataTableColumn<ArUnscheduledOpportunityRow>[] =
    useMemo(
      () => [
        {
          key: 'provider',
          label: 'Provider',
          headerClassName: 'reports-patient-value-revenue__ar-provider-col',
          cellClassName: 'reports-patient-value-revenue__ar-provider-col',
        },
        {
          key: 'patients',
          label: 'Patients',
          headerClassName:
            'reports-patient-value-revenue__table-head--num reports-patient-value-revenue__ar-patients-col',
          cellClassName:
            'reports-patient-value-revenue__table-cell--num reports-patient-value-revenue__ar-patients-col',
        },
        ...(arOpportunityFullViewOnly
          ? [AR_OPPORTUNITY_UNSCHEDULED_VALUE_COLUMN]
          : []),
        {
          key: 'outstandingArValue',
          label: 'Outstanding AR value',
          headerClassName:
            'reports-patient-value-revenue__table-head--num reports-patient-value-revenue__ar-outstanding-col',
          cellClassName:
            'reports-patient-value-revenue__table-cell--num reports-patient-value-revenue__ar-outstanding-col',
        },
      ],
      [arOpportunityFullViewOnly],
    )

  const newVsExistingChartData = useMemo(() => {
    const rows = data?.newVsExistingProduction.rows ?? []
    return rows.map((row) => {
      const revenueK = row.revenue / 1000
      return {
        label: row.label,
        patients: row.patients,
        revenueK,
        barValue: newVsExistingView === 'Patients' ? row.patients : revenueK,
        topLabel:
          newVsExistingView === 'Patients'
            ? row.patients.toLocaleString('en-US')
            : formatRevenueKLabel(row.revenue),
      }
    })
  }, [data, newVsExistingView])

  const newVsExistingYAxis = useMemo(() => {
    if (newVsExistingView === 'Patients') {
      const axis = computeDynamicCountAxis(
        newVsExistingChartData.map((row) => row.barValue),
      )
      return {
        ...axis,
        tickFormatter: axis.tickFormatter,
      }
    }
    const axis = computeDynamicCountAxis(
      newVsExistingChartData.map((row) => row.barValue),
    )
    return {
      domain: axis.domain,
      ticks: axis.ticks,
      tickFormatter: (v: number) => `$${v % 1 !== 0 ? v.toFixed(1) : v}K`,
    }
  }, [newVsExistingChartData, newVsExistingView])

  const valueSegmentsChartData = useMemo(
    () =>
      valueSegmentRows.map((row) => ({
        ...row,
        barValue: valueSegmentsView === 'Count' ? row.count : row.percentage,
        topLabel:
          valueSegmentsView === 'Count'
            ? String(row.count)
            : row.percentageLabel,
      })),
    [valueSegmentRows, valueSegmentsView],
  )

  const valueSegmentsYAxis = useMemo(() => {
    if (valueSegmentsView === 'Count') {
      return computeDynamicCountAxis(
        valueSegmentsChartData.map((row) => row.barValue),
      )
    }
    return computePercentAxis(valueSegmentsChartData.map((row) => row.barValue))
  }, [valueSegmentsChartData, valueSegmentsView])

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.averagePatientValue.value,
      data?.activePatientRevenue.value,
      data?.unscheduledTreatment.value,
      data?.totalOutstandingAr.value,
    ),
  )
  const procedureMixEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.productionByProcedureMix?.total,
      ...(data?.productionByProcedureMix?.categories.flatMap((row) => [
        row.amount,
        row.percentage,
      ]) ?? []),
    ),
  )
  const valueSegmentsEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.activePatientValueSegments?.highValueRevenueShare,
      ...(data?.activePatientValueSegments?.segments.flatMap((row) => [
        row.count,
        row.percentage,
      ]) ?? []),
    ),
  )
  const newVsExistingEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.newVsExistingProduction?.rows ?? []).some((row) =>
      hasNonZeroDashboardValues(row.patients, row.revenue),
    ),
  )
  const arOpportunityEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.arUnscheduledByProvider?.rows ?? []).some((row) =>
      hasNonZeroDashboardValues(
        row.patients,
        row.unscheduledValue,
        row.outstandingAr,
      ),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-patient-value-revenue">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Patient Value & Revenue Distribution">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={PATIENT_VALUE_REVENUE_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      <ReportExpandableSection sectionId="pvr-kpi">
        {kpiEmpty ? (
          <DashboardSectionEmptyState />
        ) : (
          <ReportKpiGrid items={kpiItems} />
        )}
      </ReportExpandableSection>

      <div className="reports-patient-value-revenue__stack">
        <ReportExpandableSection sectionId="pvr-mix">
          <div className="reports-patient-value-revenue__grid">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Production by Procedure Mix
                  </ReportSectionHeading>
                </div>
                {procedureMixEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <>
                    <div className="reports-patient-value-revenue__mix-total">
                      <span className="reports-patient-value-revenue__mix-total-label">
                        Total production by procedure
                      </span>
                      <strong className="reports-patient-value-revenue__mix-total-value">
                        {procedureMixTotal}
                      </strong>
                    </div>
                    <div className="reports-patient-value-revenue__mix-content">
                      <div className="reports-patient-value-revenue__mix-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={procedureMixData}
                              dataKey="percentage"
                              nameKey="label"
                              cx="50%"
                              cy="50%"
                              outerRadius={84}
                              paddingAngle={2}
                            >
                              {procedureMixData.map((row) => (
                                <Cell key={row.id} fill={row.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value: number) => `${value}%`}
                              contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <ul className="reports-patient-value-revenue__mix-list">
                        {procedureMixData.map((row) => (
                          <li
                            key={row.id}
                            className="reports-patient-value-revenue__mix-row"
                          >
                            <span className="reports-patient-value-revenue__mix-label-group">
                              <span
                                className="reports-patient-value-revenue__mix-swatch"
                                style={{ backgroundColor: row.color }}
                              />
                              <span className="reports-patient-value-revenue__mix-label">
                                {row.label}
                              </span>
                            </span>
                            <span className="reports-patient-value-revenue__mix-values">
                              <strong className="reports-patient-value-revenue__mix-amount">
                                {row.amount}
                              </strong>
                              <span className="reports-patient-value-revenue__mix-percent">
                                {row.percentage}%
                              </span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </ReportSectionCard>

            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Active Patient Value Segments
                  </ReportSectionHeading>
                  {valueSegmentsEmpty ? null : (
                    <ReportViewTabs
                      options={REPORTS_DISTRIBUTION_VIEW_TABS}
                      value={valueSegmentsView}
                      onChange={(value) =>
                        setValueSegmentsView(value as ReportsDistributionView)
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                {valueSegmentsEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <>
                    <p className="reports-patient-value-revenue__note">
                      {highValueRevenueShare == null
                        ? '—'
                        : `${highValueRevenueShare}% of revenue comes from patients with $3,000+ in lifetime value`}
                    </p>
                    <div className="reports-patient-value-revenue__segments-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={valueSegmentsChartData}
                          margin={REPORT_BAR_CHART_MARGIN}
                          barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                        >
                          <CartesianGrid
                            vertical={false}
                            strokeDasharray="0"
                            stroke={REPORT_CHART_GRID_STROKE}
                          />
                          <XAxis
                            dataKey="label"
                            axisLine={false}
                            tickLine={false}
                            interval={0}
                            tick={REPORT_CHART_AXIS_TICK}
                            height={30}
                          />
                          <YAxis
                            domain={valueSegmentsYAxis.domain}
                            ticks={valueSegmentsYAxis.ticks}
                            allowDecimals={false}
                            axisLine={false}
                            tickLine={false}
                            tick={REPORT_CHART_AXIS_TICK}
                            width={REPORT_YAXIS_WIDTH}
                            tickFormatter={valueSegmentsYAxis.tickFormatter}
                          />
                          <Tooltip
                            formatter={(value: number) =>
                              valueSegmentsView === 'Count'
                                ? value
                                : `${value}%`
                            }
                            contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                          />
                          <Bar
                            dataKey="barValue"
                            fill="var(--blue-400)"
                            radius={[8, 8, 8, 8]}
                            barSize={100}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </div>
            </ReportSectionCard>
          </div>
        </ReportExpandableSection>

        <div className="reports-patient-value-revenue__grid">
          <ReportExpandableSection sectionId="pvr-ne">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    New vs Existing Production (Revenue Split)
                  </ReportSectionHeading>
                  {newVsExistingEmpty ? null : (
                    <ReportViewTabs
                      options={NEW_VS_EXISTING_VIEW_TABS}
                      value={newVsExistingView}
                      onChange={(value) =>
                        setNewVsExistingView(
                          value as NewVsExistingProductionView,
                        )
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                {newVsExistingEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-patient-value-revenue__new-existing-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={newVsExistingChartData}
                        margin={REPORT_BAR_CHART_MARGIN}
                        barCategoryGap={REPORT_NEW_EXISTING_BAR_CATEGORY_GAP}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="label"
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                          tick={REPORT_CHART_AXIS_TICK}
                          height={30}
                        />
                        <YAxis
                          domain={newVsExistingYAxis.domain}
                          ticks={newVsExistingYAxis.ticks}
                          tickFormatter={newVsExistingYAxis.tickFormatter}
                          allowDecimals={newVsExistingView === 'Revenue'}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={REPORT_YAXIS_WIDTH_WIDE}
                        />
                        <Tooltip
                          formatter={(value: number) =>
                            newVsExistingView === 'Patients'
                              ? value.toLocaleString('en-US')
                              : `$${value % 1 !== 0 ? value.toFixed(1) : value}K`
                          }
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Bar
                          dataKey="barValue"
                          stroke="none"
                          radius={[8, 8, 8, 8]}
                          maxBarSize={REPORT_NEW_EXISTING_MAX_BAR_SIZE}
                        >
                          {newVsExistingChartData.map((entry) => (
                            <Cell
                              key={entry.label}
                              fill={
                                entry.label === 'New Patient'
                                  ? 'var(--blue-400)'
                                  : 'var(--blue-500)'
                              }
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="pvr-ar">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column reports-patient-value-revenue__column--fill">
                <div className="reports-patient-base-health__chart-head">
                  {arOpportunityFullViewOnly ? (
                    <LinkButton
                      type="button"
                      underline={false}
                      leftIcon={<ArrowLeftIcon aria-hidden />}
                      className="reports-patient-base-health__risk-patients-title reports-patient-base-health__risk-patients-title--back"
                      onClick={handleArOpportunityExitFullView}
                      aria-label="Exit full view of AR and unscheduled treatment opportunity"
                    >
                      <ReportSectionHeading className="reports-sentence-case-title">
                        {AR_OPPORTUNITY_TITLE}
                      </ReportSectionHeading>
                    </LinkButton>
                  ) : arOpportunityEmpty ? (
                    <ReportSectionHeading className="reports-sentence-case-title">
                      {AR_OPPORTUNITY_TITLE}
                    </ReportSectionHeading>
                  ) : (
                    <LinkButton
                      type="button"
                      underline={false}
                      rightIcon={<ArrowRightIcon aria-hidden />}
                      className="reports-patient-base-health__risk-patients-title"
                      onClick={handleArOpportunityFullView}
                      disabled={expandCtx == null}
                      aria-label="Open full view of AR and unscheduled treatment opportunity"
                    >
                      <ReportSectionHeading className="reports-sentence-case-title">
                        {AR_OPPORTUNITY_TITLE}
                      </ReportSectionHeading>
                    </LinkButton>
                  )}
                </div>
                {arOpportunityEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-patient-value-revenue__opportunity-table">
                    <ReportDataTable
                      key={
                        arOpportunityFullViewOnly ? 'pvr-ar-fv' : 'pvr-ar-card'
                      }
                      columns={arOpportunityTableColumns}
                      rows={arOpportunityTableRows}
                      rowKey={(row) => row.id}
                      className={[
                        'reports-patient-value-revenue__provider-table',
                        'reports-patient-value-revenue__ar-opportunity-table',
                        arOpportunityFullViewOnly
                          ? 'reports-patient-value-revenue__provider-table--full-view'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      showFullViewButton={false}
                      showExitFullViewButton={false}
                      showPagination={arOpportunityFullViewOnly}
                      fullViewSectionId="pvr-ar"
                      fullViewSearchWithFilter
                    />
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>
      </div>
    </div>
  )
}

export default PatientValueRevenueReport
